import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Briefcase,
  CalendarDays,
  FileText,
  LogOut,
  Menu,
  Satellite,
  TrendingUp,
  Clapperboard,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { LanguageSelect } from '@/components/LanguageSelect';
import { useIsWideViewport } from '@/hooks/useIsWideViewport';
import { navT } from '@/i18n/messages/nav';
import { useAuth } from '@/lib/auth-context';
import { activeNavItem, navSectionsFor, type NavItemId } from '@/lib/navSections';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

/// Breakpoint `md` do Tailwind: a partir dele a barra fixa substitui o drawer.
const MD_QUERY = '(min-width: 768px)';

/// #936 — casca das telas de gestão: menu lateral fixo no lugar dos botões que
/// cada página desenhava no próprio cabeçalho.
///
/// Três formas, pela largura:
///  - **≥1024 px (`lg`)**: barra de 232 px com rótulo;
///  - **768–1023 px (`md`)**: só ícones (o nome vai no `aria-label`/`title`);
///  - **<768 px**: barra superior com botão de menu, que abre um drawer.
///
/// Wizard, painel ao vivo e replays ficam **fora** de propósito: são telas de
/// foco (o live é o mapa inteiro) e têm o próprio "Voltar".
///
/// `children` existe pro NotFound, que não é rota filha (ver `App.tsx`).
export function AppShell({ children }: { children?: ReactNode }) {
  const t = navT();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Navegou → fecha o drawer. Pela rota, e não pelo clique no link: cobre
  // também o "voltar" do navegador com o drawer aberto. Pela `key`, e não pelo
  // `pathname`: tocar no item da página atual navega pra mesma URL, o pathname
  // não muda e o drawer ficava aberto, parecendo que o toque não fez nada.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.key]);

  // Girou o celular pra paisagem (≥768 px) com o drawer aberto: o `md:hidden`
  // esconde o drawer, mas o modal do Radix seguiria aberto, com a rolagem
  // travada e `pointer-events: none` no body. Fecha junto.
  const isMdUp = useIsWideViewport(MD_QUERY);
  useEffect(() => {
    if (isMdUp) setDrawerOpen(false);
  }, [isMdUp]);

  return (
    <div className="min-h-screen">
      {/* `print:hidden` em tudo que é menu: a página de resultados é feita pra
          imprimir, e elemento `fixed` sai repetido em toda folha. */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-16 md:flex lg:w-[232px] print:!hidden">
        <Sidebar compactUntilLg />
      </aside>

      {/* Celular: barra superior + drawer. O Radix Dialog cuida do que um
          modal promete e é fácil esquecer à mão: foco preso dentro, rolagem do
          fundo travada, Esc, foco de volta no botão ao fechar, `aria-modal`. */}
      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-[#0B1220] px-4 text-white md:hidden print:hidden">
          <Dialog.Trigger
            aria-label={t.openMenu}
            className="-ml-2 flex h-10 w-10 items-center justify-center rounded-lg hover:bg-[#162036]"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Dialog.Trigger>
          <BrandSignature />
        </div>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 md:hidden print:hidden" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 flex w-[260px] max-w-[85vw] md:hidden print:hidden"
          >
            <Dialog.Title className="sr-only">{t.menuLabel}</Dialog.Title>
            <Sidebar
              headerAction={
                <Dialog.Close
                  aria-label={t.closeMenu}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#C7D0E0] hover:bg-[#162036] hover:text-white"
                >
                  <X className="h-5 w-5" aria-hidden />
                </Dialog.Close>
              }
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="md:pl-16 lg:pl-[232px] print:!pl-0">{children ?? <Outlet />}</div>
    </div>
  );
}

const ICONS: Record<NavItemId, LucideIcon> = {
  events: CalendarDays,
  results: FileText,
  users: Users,
  trackers: Satellite,
  business: Briefcase,
  growth: TrendingUp,
  flyover: Clapperboard,
};

/// Conteúdo da barra. `compactUntilLg` = só ícones entre `md` e `lg` (a barra
/// fixa do tablet); o drawer é sempre completo.
function Sidebar({ compactUntilLg = false, headerAction }: { compactUntilLg?: boolean; headerAction?: ReactNode }) {
  const t = navT();
  const { user, signOut } = useAuth();
  // E-mail do perfil do painel (o mesmo que carrega os papéis); o do Firebase
  // fica de reserva.
  const email = useAuthStore((s) => s.user?.email) ?? user?.email ?? null;
  const isOrganizer = useAuthStore((s) => s.hasRole('organizer'));
  const isVerifiedOrganizer = useAuthStore((s) => s.hasRole('verified_organizer'));
  const { pathname } = useLocation();
  const active = activeNavItem(pathname);
  // Rótulo escondido no modo ícone, mas ainda lido pelo leitor de tela.
  const labelClass = compactUntilLg ? 'sr-only lg:not-sr-only' : undefined;

  return (
    <nav
      aria-label={t.menuLabel}
      className="flex h-full w-full flex-col overflow-y-auto bg-[#0B1220] px-3 py-4 text-[#C7D0E0]"
    >
      <div className={cn('flex items-center justify-between gap-2 px-2 pb-4', compactUntilLg && 'justify-center lg:justify-between')}>
        {compactUntilLg ? (
          <>
            <BrandMark className="lg:hidden" />
            <BrandSignature className="hidden lg:inline-block" />
          </>
        ) : (
          <BrandSignature />
        )}
        {headerAction}
      </div>

      {navSectionsFor({ isOrganizer, isVerifiedOrganizer }).map((section, i) => (
        <div key={section.id} className="flex flex-col gap-1">
          {/* Título da seção. No modo ícone ele não cabe: vira um divisor (o
              leitor de tela ainda ouve o nome). Não usar `not-sr-only` aqui:
              ele zera o padding e o título encosta na borda. */}
          {compactUntilLg && i > 0 && <hr className="mx-2 my-2 border-[#1E2A40] lg:hidden" aria-hidden />}
          <p
            className={cn(
              'px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-[#7E8AA3]',
              compactUntilLg && 'sr-only lg:not-sr-only lg:px-3 lg:pb-1 lg:pt-3',
            )}
          >
            {t.sections[section.id]}
          </p>
          {section.items.map((item) => {
            const Icon = ICONS[item.id];
            const isActive = active === item.id;
            const label = t.items[item.id];
            return (
              <Link
                key={item.id}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                title={compactUntilLg ? label : undefined}
                className={cn(
                  'flex min-h-[40px] items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  compactUntilLg && 'justify-center lg:justify-start',
                  isActive ? 'bg-[#2D59FF] text-white' : 'hover:bg-[#162036] hover:text-white',
                )}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                <span className={labelClass}>{label}</span>
              </Link>
            );
          })}
        </div>
      ))}

      <div className="flex-1" />

      <div className="flex flex-col gap-2 border-t border-[#1E2A40] px-2 pt-3">
        {email && (
          <p className={cn('truncate text-xs text-[#9AA6BD]', compactUntilLg && 'hidden lg:block')} title={email}>
            {email}
          </p>
        )}
        {/* Trocar idioma recarrega a página; aqui é seguro (telas de lista,
            sem formulário longo). No modo ícone empilha em coluna. */}
        <LanguageSelect
          className={cn('text-[#9AA6BD]', compactUntilLg && 'flex-col lg:flex-row items-center lg:items-start')}
        />
        <button
          type="button"
          onClick={() => signOut()}
          title={compactUntilLg ? t.signOut : undefined}
          className={cn(
            'flex min-h-[36px] items-center gap-2.5 rounded-lg px-1 py-1.5 text-sm font-medium hover:text-white',
            compactUntilLg && 'justify-center lg:justify-start',
          )}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" aria-hidden />
          <span className={labelClass}>{t.signOut}</span>
        </button>
      </div>
    </nav>
  );
}

/// Assinatura clara (a barra é escura nos dois temas). Arquivo canônico em
/// `public/brand/`, mesmo do `WindMapsSignature`; aqui só a variante branca.
function BrandSignature({ className }: { className?: string }) {
  return (
    <img
      src="/brand/windmaps_signature_spinning_branca.svg"
      alt="WindMaps"
      width={120}
      height={42}
      className={cn('h-[42px] w-[120px]', className)}
    />
  );
}

/// Marca reduzida (isóbaras concêntricas) do modo ícone, onde a assinatura não
/// cabe nos 120 px mínimos do guideline.
function BrandMark({ className }: { className?: string }) {
  return (
    <span
      className={cn('flex h-8 w-8 items-center justify-center rounded-full bg-[#2CAFFF]', className)}
      aria-hidden
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2D84FF]">
        <span className="h-2.5 w-2.5 rounded-full bg-[#2E2EFF]" />
      </span>
    </span>
  );
}
